import React from 'react';
import { ImageViewer } from './ImageViewer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { contentStyles } from './ContentStylesConfig';

// Shared markdown component configuration
export const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  return (
    <div className={`${contentStyles.text} ${contentStyles.markdownContainer}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({...props}) => (
            <h1 className="text-3xl font-bold text-gray-900 mt-10 mb-6" {...props} />
          ),
          h2: ({...props}) => (
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-5" {...props} />
          ),
          h3: ({...props}) => (
            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" {...props} />
          ),
          h4: ({...props}) => (
            <h4 className="text-lg font-semibold text-gray-900 mt-5 mb-3" {...props} />
          ),
          h5: ({...props}) => (
            <h5 className="text-base font-semibold text-gray-900 mt-4 mb-2" {...props} />
          ),
          h6: ({...props}) => (
            <h6 className="text-sm font-semibold text-gray-900 mt-3 mb-2" {...props} />
          ),
          img: ({ ...props }) => {
            const { src, alt } = props;
            return <ImageViewer imageUrl={src || ''} alt={alt || ''} />;
          },
          p: ({ children }) => {
            // Check if this paragraph contains only an image
            const containsOnlyImage =
              React.Children.count(children) === 1 &&
              React.isValidElement(children) &&
              (children.type === ImageViewer ||
                (typeof children.type === 'function' &&
                  children.type.name === 'ImageViewer'));

            // If it's only an image, remove all margins
            return (
              <p className={containsOnlyImage ? 'm-0 p-0 !mt-0 !mb-0' : 'mb-4'}>{children}</p>
            );
          },
          ul: ({ children, ...props }) => (
            <ul className="mb-6 pl-8 list-disc" {...props}>{children}</ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="mb-6 pl-8 list-decimal" {...props}>{children}</ol>
          ),
          li: ({ children, ...props }) => (
            <li className="mb-3" {...props}>{children}</li>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote className="pl-4 border-l-4 border-gray-200 italic text-gray-600 my-4" {...props}>{children}</blockquote>
          ),
          code: ({inline, ...props}) => (
            inline 
              ? <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-800" {...props} />
              : <code className="block bg-gray-100 p-4 rounded text-gray-800 overflow-x-auto" {...props} />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};